<script setup lang="ts">
// icons
import { faUndoAlt } from '@fortawesome/pro-regular-svg-icons';

// components
import CiFilterGroup from '@/components/filter/CiFilterPanel/CiFilterGroup.vue';

/********************
 * PROPS & EMITS     *
 ********************/
export interface CiFilterPanelProps {
  groups: any[];
  selectedFilter: any;
  selectedFilterGroup: string | null;
}
const props = withDefaults(defineProps<CiFilterPanelProps>(), {
});

const emit = defineEmits<{
  'detail-rating': [];
  'input': [value: { filter: string; value: any }];
  'reset': [group?: string | null, excluded?: string[]];
  'setFilterGroup': [group: string | null];
}>();

/********************
 * COMPOSITIONS      *
 ********************/
const { $gettext } = useGettext();
const {
  activeFilterCount,
  getActiveFilterByGroup,
  getFilterByGroupName,
  getFilterByName,
} = useFilter();

/********************
 * REFS & VARS       *
 ********************/
const currentlyOpen = ref<number | null>(null);

const overallRatingFilter = getFilterByName('rating_avg_overall__gte');

onMounted(() => {
  const index = props.groups.findIndex(group => group.name === props.selectedFilterGroup);
  currentlyOpen.value = index < 0 ? null : index;
});

/********************
 * FUNCTIONS         *
 ********************/
function onToggleAccordion(name: string, index: number) {
  currentlyOpen.value = props.selectedFilterGroup === name ? null : index;
  const group = props.selectedFilterGroup === name ? null : name;
  emit('setFilterGroup', group);
}
</script>

<template>
  <div class="filter-panel--search lg:max-h-full">
    <div class="my-4 font-medium">
      {{ $gettext('Narrow down search:') }}
    </div>

    <!-- overall rating -->
    <div class="mb-4 rounded-[0.313rem] border border-[#adb5bd] bg-white pt-6">
      <div class="container">
        <div class="row">
          <div class="col-12">
            <div class="mb-3 font-medium">
              {{ $gettext('Overall rating') }}
            </div>

            <CiFilterRating
              class="mb-4"
              :inverse="overallRatingFilter.inverse"
              :max="overallRatingFilter.max"
              :name="overallRatingFilter.name"
              :value="props.selectedFilter['rating_avg_overall__gte']"
              :display-value="overallRatingFilter.displayValue"
              @input="emit('input', { filter: 'rating_avg_overall__gte', value: $event })"
            />

            <CiButton
              tag="button"
              variant="text"
              size="small"
              class="mb-4"
              @click="emit('detail-rating')"
            >
              {{ $gettext('Detailed ratings') }}
            </CiButton>

            <div v-if="props.selectedFilter['rating_avg_overall__gte']">
              <hr class="my-4 h-px border-0 bg-gray-40" />
              <CiButton
                tag="button"
                variant="secondary"
                class="mb-4 w-full"
                @click="emit('input', { filter: 'rating_avg_overall__gte', value: 0 })"
              >
                <CiAwesomeIcon
                  :icon="faUndoAlt"
                  class="mr-2"
                  ratio="0.5"
                />
                {{ $gettext('Reset Filter') }}
              </CiButton>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- groups -->
    <CiAccordion
      v-for="(group, index) in props.groups"
      ref="accordion"
      :key="`group-${index}`"
      :class="{
        'first': index === 0,
        'after-open': currentlyOpen === null ? false : index === currentlyOpen + 1,
      }"
      :is-last="index === props.groups.length - 1"
      :show="props.selectedFilterGroup === group.name"
      :scroll-to-item="false"
      :remove-collapsed-markup="true"
      @accordion-toggle="onToggleAccordion(group.name, index)"
    >
      <template #header>
        <div class="shrink grow basis-auto hyphens-auto py-6">
          <div
            :class="{ 'font-medium': props.selectedFilterGroup === group.name }"
            class="label"
          >
            {{ group.label }}
          </div>
          <div
            v-if="getActiveFilterByGroup(group.name, ['rating_avg_overall__gte']).length > 0"
            class="text-small-book mt-1 truncate text-gray"
            v-text="getActiveFilterByGroup(group.name, ['rating_avg_overall__gte']).join(', ')"
          />
        </div>
      </template>
      <div class="container pb-2 pt-3">
        <CiFilterGroup
          :filter="getFilterByGroupName(group.name, ['rating_avg_overall__gte'])"
          :selected-filter="props.selectedFilter"
          :is-panel="true"
          @change="emit('input', $event)"
        />
        <div
          v-if="getActiveFilterByGroup(group.name, ['rating_avg_overall__gte']).length > 0"
          class="row"
        >
          <div class="col-12">
            <hr class="my-4 h-px border-0 bg-gray-40" />
            <CiButton
              tag="button"
              variant="secondary"
              class="mb-4 w-full"
              @click="emit('reset', group.name || null, ['rating_avg_overall__gte'])"
            >
              <CiAwesomeIcon
                :icon="faUndoAlt"
                class="mr-2"
                ratio="0.5"
              />
              {{ $gettext('Reset Filter') }}
            </CiButton>
          </div>
        </div>
      </div>
    </CiAccordion>

    <!-- reset all filter -->
    <template v-if="activeFilterCount">
      <hr class="my-4 h-px border-0 bg-gray-40" />
      <div class="px-2 pb-2">
        <CiButton
          tag="button"
          variant="secondary"
          class="w-full"
          @click="emit('reset')"
        >
          <CiAwesomeIcon
            :icon="faUndoAlt"
            class="mr-2"
            ratio="0.5"
          />
          {{ $gettext('Reset All Filter') }}
        </CiButton>
      </div>
    </template>
  </div>
</template>

<style lang="postcss" scoped>
.filter-panel--search :deep(.accordion.first) .accordion__header {
  @apply border-none;
}

.filter-panel--search :deep(.accordion.after-open) .accordion__header {
  @apply border-none;
}

.filter-panel--search :deep(.accordion) .accordion__header {
  @apply border-b-0;
}

.filter-panel--search :deep(.accordion.open) {
  @apply rounded-[0.313rem] border border-[#adb5bd] bg-white;
}

.filter-panel--search :deep(.accordion.open) .accordion__header {
  @apply border-b border-t-0;
}

.filter-panel--search :deep(.accordion.open) .accordion__body {
  @apply border-b-0;
}

.filter-panel--search :deep(.accordion.open) .accordion__icon {
  @apply fill-black;
}
</style>
